<template>
  <!-- =============================================================================
       CAPTCHA — CLOUDFLARE TURNSTILE
       -----------------------------------------------------------------------------
       Se pinta solo si hay una site key configurada en src/content/site.js. Si no
       la hay, el componente no existe y los formularios siguen funcionando igual
       que antes: así se puede desplegar el código antes de dar de alta el widget.

       Quien valida el token NO es esta página, es Formspree: en el panel del
       formulario hay que activar CAPTCHA, elegir Cloudflare Turnstile y pegar el
       Secret Key. Sin ese paso el token viaja pero nadie lo comprueba.

       El token caduca a los 5 minutos y es de un solo uso, de ahí el
       expired-callback y el reinicio después de cada envío.
       ============================================================================= -->
  <div v-if="siteKeyActiva" class="gr-captcha">
    <div ref="widget"></div>
    <p v-if="fallo" class="gr-captcha-aviso">{{ textoFallo }}</p>
  </div>
</template>

<script>
  var SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var promesaScript = null;

  /* Carga el script de Turnstile una sola vez por sesión, aunque haya varios
     formularios en la misma página. */
  function cargarTurnstile() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.reject(new Error('sin navegador'));
    }
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (promesaScript) return promesaScript;

    promesaScript = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = SRC;
      s.async = true;
      s.defer = true;

      s.onload = function () {
        var intentos = 0;
        (function esperar() {
          if (window.turnstile) return resolve(window.turnstile);
          if (++intentos > 100) return reject(new Error('turnstile no disponible'));
          setTimeout(esperar, 50);
        })();
      };

      s.onerror = function () {
        promesaScript = null;
        reject(new Error('no se pudo cargar turnstile'));
      };

      document.head.appendChild(s);
    });

    return promesaScript;
  }

  var AVISOS = {
    es: 'No hemos podido cargar la verificación antispam. Recarga la página o escríbenos por WhatsApp.',
    ca: 'No hem pogut carregar la verificació antispam. Recarrega la pàgina o escriu-nos per WhatsApp.'
  };

  export default {
    name: 'CaptchaTurnstile',

    props: {
      siteKey: { type: String, default: '' },
      lang: { type: String, default: 'es' },
      theme: { type: String, default: 'light' }
    },

    data() {
      return {
        widgetId: null,
        fallo: false
      };
    },

    computed: {
      siteKeyActiva() {
        return this.siteKey || '';
      },

      textoFallo() {
        return AVISOS[this.lang] || AVISOS.es;
      }
    },

    mounted() {
      if (this.siteKeyActiva) this.pintar();
    },

    beforeDestroy() {
      if (this.widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(this.widgetId); } catch (e) { /* nada */ }
      }
    },

    methods: {
      pintar() {
        var self = this;
        cargarTurnstile()
          .then(function (turnstile) {
            if (!self.$refs.widget) return;
            self.widgetId = turnstile.render(self.$refs.widget, {
              sitekey: self.siteKeyActiva,
              theme: self.theme,
              /* Turnstile no tiene catalán; el castellano es el más cercano */
              language: 'es',
              callback: function (token) {
                self.fallo = false;
                self.$emit('token', token);
              },
              'expired-callback': function () {
                self.$emit('token', '');
                self.reiniciar();
              },
              'error-callback': function () {
                self.fallo = true;
                self.$emit('token', '');
              }
            });
          })
          .catch(function () {
            self.fallo = true;
            self.$emit('token', '');
          });
      },

      /* Un token solo vale para un envío: después de cada uno hay que pedir
         otro. Lo llaman los formularios desde el mixin de captcha. */
      reiniciar() {
        if (this.widgetId === null || !window.turnstile) return;
        try { window.turnstile.reset(this.widgetId); } catch (e) { /* nada */ }
      }
    }
  };
</script>

<style scoped>
  .gr-captcha {
    margin: 4px 0 16px;
  }

  .gr-captcha-aviso {
    font-size: 0.88rem;
    line-height: 1.5;
    color: #B71C1C;
    margin: 8px 0 0;
  }
</style>
